import React, { useState } from 'react';
import { SearchIcon, FilterIcon, XIcon, SlidersIcon, CalendarIcon, UsersIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
interface TourFiltersProps {
  activeFilter: string;
  setActiveFilter: (filter: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  sortOption: string;
  setSortOption: (option: string) => void;
  viewMode: string;
  setViewMode: (mode: string) => void;
  filtersOpen: boolean;
  setFiltersOpen: (open: boolean) => void;
}
export function TourFilters({
  activeFilter,
  setActiveFilter,
  searchQuery,
  setSearchQuery,
  sortOption,
  setSortOption,
  viewMode,
  setViewMode,
  filtersOpen,
  setFiltersOpen
}: TourFiltersProps) {
  const [priceRange, setPriceRange] = useState([500, 5000]);
  const [duration, setDuration] = useState([1, 14]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedTravelStyles, setSelectedTravelStyles] = useState<string[]>([]);
  const [selectedInclusions, setSelectedInclusions] = useState<string[]>([]);
  const [groupSize, setGroupSize] = useState(10);
  const filterCategories = [{
    id: 'all',
    label: 'All Tours'
  }, {
    id: 'group',
    label: 'Group Tours'
  }, {
    id: 'flexi',
    label: 'Flexi Tours'
  }, {
    id: 'theme',
    label: 'Theme Tours'
  }, {
    id: 'cruises',
    label: 'Cruises'
  }];
  const tourTypes = [{
    id: 'group',
    label: 'Group Tours',
    icon: '👥'
  }, {
    id: 'private',
    label: 'Private Tours',
    icon: '🔒'
  }, {
    id: 'self',
    label: 'Self-Guided',
    icon: '🧭'
  }, {
    id: 'adventure',
    label: 'Adventure',
    icon: '🏔️'
  }, {
    id: 'cultural',
    label: 'Cultural',
    icon: '🏛️'
  }, {
    id: 'relaxation',
    label: 'Relaxation',
    icon: '🧘'
  }, {
    id: 'photography',
    label: 'Photography',
    icon: '📸'
  }, {
    id: 'culinary',
    label: 'Culinary',
    icon: '🍽️'
  }, {
    id: 'wellness',
    label: 'Wellness',
    icon: '💆'
  }];
  const travelStyles = [{
    id: 'family',
    label: 'Family-friendly'
  }, {
    id: 'couples',
    label: 'Couples'
  }, {
    id: 'solo',
    label: 'Solo travelers'
  }, {
    id: 'accessible',
    label: 'Accessible tours'
  }, {
    id: 'eco',
    label: 'Eco-friendly'
  }, {
    id: 'luxury',
    label: 'Luxury experiences'
  }, {
    id: 'budget',
    label: 'Budget-conscious'
  }];
  const inclusions = [{
    id: 'meals',
    label: 'Meals'
  }, {
    id: 'transport',
    label: 'Transportation'
  }, {
    id: 'accommodation',
    label: 'Accommodation'
  }, {
    id: 'guides',
    label: 'Guides'
  }, {
    id: 'activities',
    label: 'Activities'
  }, {
    id: 'insurance',
    label: 'Insurance'
  }];
  const toggleCategory = (id: string) => {
    if (selectedCategories.includes(id)) {
      setSelectedCategories(selectedCategories.filter(cat => cat !== id));
    } else {
      setSelectedCategories([...selectedCategories, id]);
    }
  };
  const toggleTravelStyle = (id: string) => {
    if (selectedTravelStyles.includes(id)) {
      setSelectedTravelStyles(selectedTravelStyles.filter(style => style !== id));
    } else {
      setSelectedTravelStyles([...selectedTravelStyles, id]);
    }
  };
  const toggleInclusion = (id: string) => {
    if (selectedInclusions.includes(id)) {
      setSelectedInclusions(selectedInclusions.filter(inc => inc !== id));
    } else {
      setSelectedInclusions([...selectedInclusions, id]);
    }
  };
  const clearAllFilters = () => {
    setSelectedCategories([]);
    setSelectedTravelStyles([]);
    setSelectedInclusions([]);
    setPriceRange([500, 5000]);
    setDuration([1, 14]);
    setGroupSize(10);
  };
  return <section className="sticky top-0 z-20 bg-white shadow-md" data-id="element-1664">
      <div className="container mx-auto px-4" data-id="element-1665">
        {/* Filter bar */}
        <div className="py-4 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1666">
          <div className="flex overflow-x-auto hide-scrollbar space-x-2 pb-2 md:pb-0" data-id="element-1667">
            {filterCategories.map(category => <button key={category.id} onClick={() => setActiveFilter(category.id)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${activeFilter === category.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1668">
                {category.label}
              </button>)}
          </div>
          <div className="flex items-center space-x-3" data-id="element-1669">
            <div className="relative" data-id="element-1670">
              <input type="text" placeholder="Search tours..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 w-full md:w-64 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1671" />
              <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-1672" />
            </div>
            <button onClick={() => setFiltersOpen(true)} className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-1673">
              <FilterIcon size={16} className="mr-2" data-id="element-1674" />
              Filters
            </button>
            <div className="hidden md:block relative" data-id="element-1675">
              <select value={sortOption} onChange={e => setSortOption(e.target.value)} className="appearance-none pl-4 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-1676">
                <option value="recommended" data-id="element-1677">Recommended</option>
                <option value="price-low" data-id="element-1678">Price: Low to High</option>
                <option value="price-high" data-id="element-1679">Price: High to Low</option>
                <option value="duration" data-id="element-1680">Duration</option>
                <option value="rating" data-id="element-1681">Rating</option>
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" data-id="element-1682">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-1683">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-1684" />
                </svg>
              </div>
            </div>
            <div className="hidden md:flex items-center border border-gray-300 rounded-md overflow-hidden" data-id="element-1685">
              <button onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? 'bg-gray-100' : 'bg-white'}`} data-id="element-1686">
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-1687">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" data-id="element-1688" />
                </svg>
              </button>
              <button onClick={() => setViewMode('list')} className={`p-2 ${viewMode === 'list' ? 'bg-gray-100' : 'bg-white'}`} data-id="element-1689">
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-1690">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" data-id="element-1691" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Advanced filter modal */}
      {filtersOpen && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-1692">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0" data-id="element-1693">
            <div className="fixed inset-0 transition-opacity" onClick={() => setFiltersOpen(false)} data-id="element-1694">
              <div className="absolute inset-0 bg-gray-500 bg-opacity-75" data-id="element-1695"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" data-id="element-1696">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1697">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-1698">
                  Filter Tours
                </h3>
                <button onClick={() => setFiltersOpen(false)} className="text-gray-400 hover:text-gray-500" data-id="element-1699">
                  <XIcon size={24} data-id="element-1700" />
                </button>
              </div>
              <div className="px-6 py-4 max-h-[70vh] overflow-y-auto" data-id="element-1701">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1702">
                  {/* Duration filter */}
                  <div data-id="element-1703">
                    <h4 className="font-medium text-gray-900 mb-4" data-id="element-1704">Duration</h4>
                    <div className="mb-6" data-id="element-1705">
                      <div className="flex justify-between mb-2" data-id="element-1706">
                        <span className="text-sm text-gray-600" data-id="element-1707">
                          {duration[0]} days
                        </span>
                        <span className="text-sm text-gray-600" data-id="element-1708">
                          {duration[1]} days
                        </span>
                      </div>
                      <input type="range" min="1" max="21" value={duration[1]} onChange={e => setDuration([duration[0], parseInt(e.target.value)])} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" data-id="element-1709" />
                    </div>
                    <div className="flex space-x-2 mb-6" data-id="element-1710">
                      <button className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" data-id="element-1711">
                        Weekend
                      </button>
                      <button className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" data-id="element-1712">
                        Week
                      </button>
                      <button className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" data-id="element-1713">
                        Extended
                      </button>
                    </div>
                  </div>
                  {/* Price range */}
                  <div data-id="element-1714">
                    <h4 className="font-medium text-gray-900 mb-4" data-id="element-1715">
                      Price Range (CHF)
                    </h4>
                    <div className="mb-6" data-id="element-1716">
                      <div className="flex justify-between mb-2" data-id="element-1717">
                        <span className="text-sm text-gray-600" data-id="element-1718">
                          CHF {priceRange[0]}
                        </span>
                        <span className="text-sm text-gray-600" data-id="element-1719">
                          CHF {priceRange[1]}
                        </span>
                      </div>
                      <input type="range" min="500" max="5000" step="100" value={priceRange[1]} onChange={e => setPriceRange([priceRange[0], parseInt(e.target.value)])} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" data-id="element-1720" />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-6" data-id="element-1721">
                      <button className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" data-id="element-1722">
                        Budget
                      </button>
                      <button className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" data-id="element-1723">
                        Standard
                      </button>
                      <button className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" data-id="element-1724">
                        Luxury
                      </button>
                      <button className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" data-id="element-1725">
                        Ultra-Luxury
                      </button>
                    </div>
                  </div>
                  {/* Tour type */}
                  <div data-id="element-1726">
                    <h4 className="font-medium text-gray-900 mb-4" data-id="element-1727">
                      Tour Type
                    </h4>
                    <div className="grid grid-cols-3 gap-3 mb-6" data-id="element-1728">
                      {tourTypes.map(type => <div key={type.id} onClick={() => toggleCategory(type.id)} className={`p-3 border rounded-lg text-center cursor-pointer transition-colors ${selectedCategories.includes(type.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1729">
                          <div className="text-2xl mb-1" data-id="element-1730">{type.icon}</div>
                          <div className="text-sm font-medium" data-id="element-1731">
                            {type.label}
                          </div>
                        </div>)}
                    </div>
                  </div>
                  {/* Travel style */}
                  <div data-id="element-1732">
                    <h4 className="font-medium text-gray-900 mb-4" data-id="element-1733">
                      Travel Style
                    </h4>
                    <div className="grid grid-cols-2 gap-2 mb-6" data-id="element-1734">
                      {travelStyles.map(style => <div key={style.id} className="flex items-center" data-id="element-1735">
                          <input type="checkbox" id={`style-${style.id}`} checked={selectedTravelStyles.includes(style.id)} onChange={() => toggleTravelStyle(style.id)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1736" />
                          <label htmlFor={`style-${style.id}`} className="ml-2 text-sm text-gray-700" data-id="element-1737">
                            {style.label}
                          </label>
                        </div>)}
                    </div>
                  </div>
                  {/* Departure dates */}
                  <div data-id="element-1738">
                    <h4 className="font-medium text-gray-900 mb-4" data-id="element-1739">
                      Departure Dates
                    </h4>
                    <div className="mb-6" data-id="element-1740">
                      <button className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-1741">
                        <div className="flex items-center" data-id="element-1742">
                          <CalendarIcon size={16} className="mr-2" data-id="element-1743" />
                          <span data-id="element-1744">Select dates</span>
                        </div>
                        <ChevronDownIcon size={16} data-id="element-1745" />
                      </button>
                      <div className="mt-3 flex items-center" data-id="element-1746">
                        <input type="checkbox" id="flexible-dates" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1747" />
                        <label htmlFor="flexible-dates" className="ml-2 text-sm text-gray-700" data-id="element-1748">
                          Flexible dates
                        </label>
                      </div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-6" data-id="element-1749">
                      <h5 className="text-sm font-medium text-blue-800 mb-2" data-id="element-1750">
                        Seasonal Recommendations
                      </h5>
                      <div className="text-xs text-blue-700" data-id="element-1751">
                        <p className="mb-1" data-id="element-1752">
                          • Summer (Jun-Aug): Best for hiking and outdoor
                          activities
                        </p>
                        <p className="mb-1" data-id="element-1753">
                          • Winter (Dec-Mar): Ideal for skiing and snow
                          experiences
                        </p>
                        <p data-id="element-1754">• Spring/Fall: Less crowded with pleasant weather</p>
                      </div>
                    </div>
                  </div>
                  {/* Group size */}
                  <div data-id="element-1755">
                    <h4 className="font-medium text-gray-900 mb-4" data-id="element-1756">
                      Group Size
                    </h4>
                    <div className="mb-3" data-id="element-1757">
                      <div className="flex justify-between mb-2" data-id="element-1758">
                        <span className="text-sm text-gray-600" data-id="element-1759">1 person</span>
                        <span className="text-sm text-gray-600" data-id="element-1760">
                          {groupSize} people
                        </span>
                      </div>
                      <input type="range" min="1" max="20" value={groupSize} onChange={e => setGroupSize(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" data-id="element-1761" />
                    </div>
                    <div className="flex items-center mb-6" data-id="element-1762">
                      <input type="checkbox" id="small-groups" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1763" />
                      <label htmlFor="small-groups" className="ml-2 text-sm text-gray-700" data-id="element-1764">
                        Small groups only (max 8 people)
                      </label>
                    </div>
                  </div>
                  {/* Inclusions */}
                  <div className="md:col-span-2" data-id="element-1765">
                    <h4 className="font-medium text-gray-900 mb-4" data-id="element-1766">
                      Inclusions
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6" data-id="element-1767">
                      {inclusions.map(inclusion => <div key={inclusion.id} className="flex items-center" data-id="element-1768">
                          <input type="checkbox" id={`inclusion-${inclusion.id}`} checked={selectedInclusions.includes(inclusion.id)} onChange={() => toggleInclusion(inclusion.id)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1769" />
                          <label htmlFor={`inclusion-${inclusion.id}`} className="ml-2 text-sm text-gray-700" data-id="element-1770">
                            {inclusion.label}
                          </label>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center" data-id="element-1771">
                <div data-id="element-1772">
                  <button onClick={clearAllFilters} className="text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-1773">
                    Clear All
                  </button>
                  <span className="ml-3 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full" data-id="element-1774">
                    {selectedCategories.length + selectedTravelStyles.length + selectedInclusions.length}{' '}
                    filters active
                  </span>
                </div>
                <div className="flex space-x-3" data-id="element-1775">
                  <button onClick={() => setFiltersOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1776">
                    Cancel
                  </button>
                  <button onClick={() => setFiltersOpen(false)} className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700" data-id="element-1777">
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </section>;
}